// src/pages/Documentation/components/SidebarSearch.js
import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import debounce from 'lodash/debounce';
import './SidebarSearch.scss';
import { useClickOutside } from '../../hooks/useClickOutside';

export const SidebarSearch = ({ structure, onSectionChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showOverlay, setShowOverlay] = useState(false);
  const [error, setError] = useState(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  useClickOutside(searchRef, () => {
    setIsFocused(false);
  });

  const getAllSections = useCallback(() => {
    const sections = [];

    structure.forEach((item) => {
      if (item.type === 'link') {
        sections.push(item);
      } else if (item.type === 'group' && item.sections) {
        sections.push(
          ...item.sections.map((section) => ({
            ...section,
            group: item.title,
          }))
        );
      }
    });

    return sections;
  }, [structure]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        setError(null);
        return;
      }

      setIsSearching(true);
      setError(null);
      const sections = getAllSections();

      try {
        const results = await Promise.all(
          sections.map(async (section) => {
            try {
              const response = await fetch(section.path);
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              const content = await response.text();

              const matchesTitle = section.title
                .toLowerCase()
                .includes(query.toLowerCase());
              const matchesContent = content
                .toLowerCase()
                .includes(query.toLowerCase());

              if (matchesTitle || matchesContent) {
                return {
                  ...section,
                  matchType: matchesTitle ? 'title' : 'content',
                  snippet: matchesContent
                    ? extractSnippet(content, query)
                    : null,
                };
              }
            } catch (error) {
              console.error(`Error searching in ${section.path}:`, error);
            }
            return null;
          })
        );

        const filteredResults = results.filter((result) => result !== null);

        // Sort results: title matches first, then by group
        filteredResults.sort((a, b) => {
          if (a.matchType === 'title' && b.matchType !== 'title') return -1;
          if (a.matchType !== 'title' && b.matchType === 'title') return 1;
          return (a.group || '').localeCompare(b.group || '');
        });

        setSearchResults(filteredResults);
      } catch (error) {
        console.error('Search error:', error);
        setError('An error occurred while searching. Please try again.');
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [getAllSections]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleKeyDown = (e) => {
    if (!searchResults.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleResultClick(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleClearSearch();
        break;
      default:
        break;
    }
  };

  const extractSnippet = (content, query) => {
    const lowerContent = content.toLowerCase();
    const index = lowerContent.indexOf(query.toLowerCase());
    if (index === -1) return '';

    const start = Math.max(0, index - 30);
    const end = Math.min(content.length, index + query.length + 30);
    let snippet = content.slice(start, end);

    if (start > 0) snippet = '...' + snippet;
    if (end < content.length) snippet = snippet + '...';

    return snippet;
  };

  const handleResultClick = (section) => {
    onSectionChange(section);
    handleClearSearch();
  };

  // Update focus handler
  const handleFocus = () => {
    setIsFocused(true);
    if (window.innerWidth <= 767) {
      setShowOverlay(true);
    }
  };

  // Update blur handler
  const handleBlur = () => {
    // Don't immediately hide the results to allow for clicking them
    setTimeout(() => {
      if (!searchRef.current?.contains(document.activeElement)) {
        setIsFocused(false);
        setShowOverlay(false);
      }
    }, 200);
  };

  // Update clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsFocused(false);
    setSelectedIndex(-1);
    setError(null);
    setShowOverlay(false);
    inputRef.current?.blur();
  };

  return (
    <>
      {showOverlay && (
        <div className="search-overlay" onClick={handleClearSearch} />
      )}

      <div className="sidebar-search mt-6" ref={searchRef}>
        <div className={`search-input-wrapper ${isFocused ? 'focused' : ''}`}>
          <i className="bi bi-search search-icon"></i>

          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              debouncedSearch(e.target.value);
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="Search documentation..."
            className="search-input"
            aria-label="Search documentation"
          />
          {searchQuery && (
            <button
              className="clear-search"
              onClick={handleClearSearch}
              aria-label="Clear search"
            >
              <i className="bi bi-x"></i>
            </button>
          )}
        </div>

        <AnimatePresence>
          {isFocused &&
            (searchResults.length > 0 || isSearching || error) &&
            searchQuery.length >= 2 && (
              <motion.div
                className="search-results scroller"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {isSearching ? (
                  <div className="searching-indicator">
                    <div className="spinner-border spinner-border-sm text-success">
                      <span className="visually-hidden">Searching...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="search-error alert alert-danger">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                  </div>
                ) : (
                  <ul className="results-list">
                    {searchResults.map((result, index) => (
                      <motion.li
                        key={result.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleResultClick(result)}
                        className={`result-item ${
                          selectedIndex === index ? 'selected' : ''
                        }`}
                      >
                        <div className="result-title">
                          <i
                            className={`bi bi-${result.matchType === 'title' ? 'star-fill' : 'file-text'} me-2`}
                          ></i>
                          {result.title}
                        </div>
                        {result.group && (
                          <div className="result-group">
                            <i className="bi bi-folder me-1"></i>
                            {result.group}
                          </div>
                        )}
                        {result.snippet && (
                          <div className="result-snippet">{result.snippet}</div>
                        )}
                      </motion.li>
                    ))}
                  </ul>
                )}
              </motion.div>
            )}
        </AnimatePresence>
      </div>
    </>
  );
};
